"use client";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import cn from "classnames";
import { type HTMLAttributes, type ReactNode, useState } from "react";
import { Autoplay, Pagination } from "swiper/modules";
import {
  Swiper,
  type SwiperClass,
  type SwiperProps,
  SwiperSlide,
} from "swiper/react";
import "swiper/css";
import "swiper/css/pagination";
import s from "./Carousel.module.scss";

export type CarouselSettings = Omit<SwiperProps, "children">;

const defaultSettings: CarouselSettings = {
  modules: [Autoplay, Pagination],
  navigation: true,
  pagination: {
    type: "bullets",
    clickable: true,
  },
  loop: true,
};

export interface CarouselProps<T>
  extends Pick<HTMLAttributes<HTMLDivElement>, "className"> {
  items: T[];
  renderItem: (item: T, index: number) => ReactNode;
  settings?: CarouselSettings;
  fullScreen?: boolean;
  smoothScrolling?: boolean;
}

export default function Carousel<T>(props: CarouselProps<T>) {
  const {
    items,
    renderItem,
    settings: customSettings,
    fullScreen,
    smoothScrolling,
    className,
  } = props;

  const [swiper, setSwiper] = useState<SwiperClass>();

  const handlePrevSlide = () => {
    swiper?.slidePrev();
  };

  const handleNextSlide = () => {
    swiper?.slideNext();
  };

  const settings: CarouselSettings = {
    ...defaultSettings,
    ...customSettings,
    breakpoints: {
      ...customSettings?.breakpoints,
    },
  };

  const wrapperClassName = cn(s.wrapper, className, {
    [s.fullScreen]: fullScreen,
    [s.smoothScrolling]: smoothScrolling,
  });

  return (
    <div className={wrapperClassName}>
      <Swiper {...settings} onSwiper={setSwiper} className={s.swiper}>
        {items.map((item, index) => (
          <SwiperSlide key={index} className={s.slide}>
            <div className={cn(s.slideContent, "informa-slide")}>
              {renderItem(item, index)}
            </div>
          </SwiperSlide>
        ))}
      </Swiper>
      {!swiper?.isLocked && (
        <>
          <div
            onClick={handlePrevSlide}
            className={cn(s.prevSlide, "prev-slide-button")}
          >
            <KeyboardArrowLeftIcon />
          </div>
          <div
            onClick={handleNextSlide}
            className={cn(s.nextSlide, "next-slide-button")}
          >
            <KeyboardArrowRightIcon />
          </div>
        </>
      )}
    </div>
  );
}
